import { type ReactNode, CSSProperties } from 'react';
import { Vector3 } from "three";

// Navigation Types
export interface NavigationItem {
  label: string;
  href: string;
}

// Common Props
export interface BaseProps {
  className?: string;
  children?: ReactNode;
}

// Layout Props
export interface SiteProps extends BaseProps {
  // Add any specific site props here
}

export interface HeaderProps extends BaseProps {
  navigationItems?: NavigationItem[];
  logoSrc?: string;
  logoAlt?: string;
  transparent?: boolean;
  // Add any specific header props here
}

// Page Section Props
export interface BannerProps extends BaseProps {
  // Add any specific banner props here
}

export interface ProblemProps extends BaseProps {
  // Add any specific problem props here
}

export interface SolutionProps extends BaseProps {
  // Add any specific solution props here
}

export interface TeamProps extends BaseProps {
  // Add any specific team props here
}

export interface DemoProps extends BaseProps {
  // Add any specific demo props here
}

// Component Props
export interface CalendlyWidgetProps extends BaseProps {
  url: string;
  styles?: CSSProperties;
}

export interface NavbarProps extends BaseProps {
  // Add any specific navbar props here
}

export interface FooterProps extends BaseProps {
  // Add any specific footer props here
}

export interface VectorDBVisualizationProps {
  className?: string;
  canvaStyles?: CSSProperties;
}

export interface VectorDBPoint {
  position: Vector3;
  speed: number;
  offset: number;
  connections: number[];
  connectionTimer: number;
}

export interface VectorDBCubeProps {
  maxConnections?: number;
  connectionDistance?: number;
  connectionDuration?: number;
  pointCount?: number;
  pointSize?: number;
  cubeSize?: number;
  rotationSpeed?: number;
}

// Problem Section Types
export interface ProblemItem {
  title: string;
  subtitle: string;
  description: string;
  icon: string;
}

export interface ProblemSectionProps extends BaseProps {
  problems?: ProblemItem[];
}

// Solution Section Types
export interface SolutionItem {
  title: string;
  description: string;
  icon: string;
}

export interface SolutionSectionProps extends BaseProps {
  solutions?: SolutionItem[];
}

// Use Case Section Types
export interface UseCaseItem {
  title: string;
  description: string;
  requestedBy: string;
  icon: string;
}

export interface UseCaseSectionProps extends BaseProps {
  cases?: UseCaseItem[];
}

// Team Section Types
export interface TeamMember {
  name: string;
  role: string;
  description: string;
  image: string;
}

export interface TeamSectionProps extends BaseProps {
  members?: TeamMember[];
}

// Image Showcase Types
export interface ShowcaseImage {
  url: string;
  alt: string;
}

export interface ImageShowcaseProps extends BaseProps {
  images?: ReadonlyArray<ShowcaseImage>;
  autoRotateInterval?: number;
  showNavigation?: boolean;
}

// Form Props
export interface ContactFormProps {
  onSubmit?: (data: ContactFormData) => void;
}

// Hero Section Props
export interface HeroSectionProps {
  className?: string;
}

export interface HeroAnimationConfig {
  duration: number;
  stiffness: number;
  damping: number;
  interval: number;
}

export const DEFAULT_HERO_ANIMATION: HeroAnimationConfig = {
  duration: 0.5,
  stiffness: 100,
  damping: 20,
  interval: 5000
} as const;

// Data Types
export interface ContactFormData {
  name: string;
  email: string;
  message: string;
}
